import type { ApiErrorDetail } from '@klareospace/pharos-lib-types';
import type { TFunction } from 'i18next';

import { MAX_IMPORT_FILE_SIZE_MB } from '../config';
import type { RejectedRow } from './import-metrics';

// ─── Constants ──────────────────────────────────────────────────────────────

// Namespace-prefixed so the mapper works regardless of the caller's default i18next namespace.
const IMPORT_ERROR_MESSAGE_KEYS: Record<string, string> = {
  'BadRequest.EmptyImportFile': 'imports:upload.errors.emptyImportFile',
  'BadRequest.FileTooLarge': 'imports:upload.errors.fileTooLarge',
  'BadRequest.InvalidXlsxFile': 'imports:upload.errors.invalidXlsxFile',
  'BadRequest.MissingImportFile': 'imports:upload.errors.missingImportFile',
  'BadRequest.MissingMandatoryColumns': 'imports:upload.errors.missingMandatoryColumns',
  'BadRequest.MissingOrdersWorksheet': 'imports:upload.errors.missingOrdersWorksheet',
  'BadRequest.UnsupportedFileType': 'imports:upload.errors.unsupportedFileType',
};

const IMPORT_REPORT_ERROR_MESSAGE_KEYS: Record<string, string> = {
  'Forbidden.ImportFileAccessDenied': 'imports:report.errors.accessDenied',
  'NotFound.ImportNotFound': 'imports:report.errors.notFound',
};

// class-validator constraint key -> translated-phrase i18n key. Only set for `validation_error`
// rejected rows (see `RejectedRow.constraints`) — absent on older imports and other error codes.
const IMPORT_CONSTRAINT_MESSAGE_KEYS: Record<string, string> = {
  isArray: 'imports:report.constraints.isArray',
  isBoolean: 'imports:report.constraints.isBoolean',
  isDate: 'imports:report.constraints.isDate',
  isDefined: 'imports:report.constraints.isDefined',
  isISO4217CurrencyCode: 'imports:report.constraints.isISO4217CurrencyCode',
  isIn: 'imports:report.constraints.isIn',
  isInt: 'imports:report.constraints.isInt',
  isNotEmpty: 'imports:report.constraints.isNotEmpty',
  isNumber: 'imports:report.constraints.isNumber',
  isString: 'imports:report.constraints.isString',
  max: 'imports:report.constraints.max',
  maxLength: 'imports:report.constraints.maxLength',
  min: 'imports:report.constraints.min',
  whitelistValidation: 'imports:report.constraints.whitelistValidation',
};

// Backend `ImportLineError.messageKey` -> translated-phrase i18n key, interpolated with
// `RejectedRow.messageArgs`. Absent on older imports and any other unmapped error code, which keep
// falling back to the raw `message`.
const IMPORT_LINE_MESSAGE_KEYS: Record<string, string> = {
  'BadRequest.ImportHandlingUnitLineLimitExceeded': 'imports:report.lineMessages.handlingUnitLineLimitExceeded',
  'BadRequest.ImportOrderGroupRejected': 'imports:report.lineMessages.orderGroupRejected',
  'BadRequest.IncorrectLine': 'imports:report.lineMessages.incorrectLine',
  'BadRequest.MissingFields': 'imports:report.lineMessages.missingFields',
  'BadRequest.OrderUpdateIneligibleValue': 'imports:report.lineMessages.ineligibleValue',
  'BadRequest.OrderUpdateNonEditableField': 'imports:report.lineMessages.nonEditableField',
  'BadRequest.UnknownSiteCode': 'imports:report.lineMessages.unknownSiteCode',
  'Conflict.CancelledOrderReimport': 'imports:report.lineMessages.cancelledOrderReimport',
  'Conflict.OrderInExecution': 'imports:report.lineMessages.inExecution',
  'Conflict.OrderTerminal': 'imports:report.lineMessages.terminal',
  'Conflict.OrderUpdateConflict': 'imports:report.lineMessages.conflict',
  'Forbidden.ScopeViolation': 'imports:report.lineMessages.scopeViolation',
  'Forbidden.SiteNotAllowed': 'imports:report.lineMessages.siteNotAllowed',
  'NotFound.OrderNotFound': 'imports:report.lineMessages.orderNotFound',
};

// ─── Utils ────────────────────────────────────────────────────────────────────

/**
 * Maps a backend `errorCode` (from the KlareoError body `POST /import/orders` returns on an
 * immediate rejection) to a localized message. Falls back to the API's own `message` when the
 * code is unmapped, then to a generic message.
 */
export function resolveImportErrorMessage(t: TFunction, detail: ApiErrorDetail): string {
  const key = detail.errorCode ? IMPORT_ERROR_MESSAGE_KEYS[detail.errorCode] : undefined;
  if (key) return t(key, { maxSizeMb: MAX_IMPORT_FILE_SIZE_MB });
  return detail.message || t('imports:upload.errors.generic');
}

/** Maps a `GET /import/:id` refusal's `errorCode` to a localized message. Never surfaces the raw backend message. */
export function resolveImportReportErrorMessage(t: TFunction, detail: ApiErrorDetail): string {
  const key = detail.errorCode ? IMPORT_REPORT_ERROR_MESSAGE_KEYS[detail.errorCode] : undefined;
  return t(key ?? 'imports:report.loadError');
}

/**
 * Resolves the rejected-rows table's "Details" cell for one row, in order: translated constraint
 * phrases when every `constraints` key is mapped (unchanged, still all-or-nothing), then a mapped
 * `messageKey` rendered with `messageArgs`, then the raw `message`. Returns `undefined` when none
 * is available. An unmapped `messageKey` falls through to the raw message exactly like an unmapped
 * constraint does.
 */
export function resolveRejectedRowDetails(t: TFunction, row: RejectedRow): string | undefined {
  const translatedConstraints = resolveConstraintPhrases(t, row.constraints);
  const translatedMessage = translatedConstraints ?? resolveMessageKeyPhrase(t, row);
  return (translatedMessage ?? row.message) || undefined;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

/** Translates every constraint key and joins them, or returns `undefined` if the list is empty/unmapped. */
function resolveConstraintPhrases(t: TFunction, constraints?: string[]): string | undefined {
  if (!constraints || constraints.length === 0) return undefined;

  const keys = constraints.map((constraint) => IMPORT_CONSTRAINT_MESSAGE_KEYS[constraint]);
  if (keys.some((key) => !key)) return undefined;

  return keys.map((key) => t(key)).join('; ');
}

/** Translates a mapped `messageKey` with its `messageArgs`, or returns `undefined` if absent/unmapped. */
function resolveMessageKeyPhrase(t: TFunction, row: RejectedRow): string | undefined {
  const key = row.messageKey ? IMPORT_LINE_MESSAGE_KEYS[row.messageKey] : undefined;
  if (!key) return undefined;

  return t(key, row.messageArgs);
}
